/**
 * DepositosView — tab "Depósitos" dentro de la vista Banco (Bloque 2, 2026-06).
 *
 * Lista de Depósitos a Plazo (DAP) + registrar uno nuevo + liquidar.
 * El capital sale de la cuenta corriente (traspaso, no gasto); al liquidar
 * vuelve con el interés. Presentacional: recibe data + handlers por props
 * (soporta preview dev-only).
 */
import { useState } from 'react';
import type {
  DepositoAPlazo,
  CrearDepositoIn,
  LiquidarDepositoIn,
} from '../../lib/api/depositos-a-plazo';
import { formatFecha } from '../../lib/format';
import { hoy } from '../../lib/tiempo';
import { Button } from '../ui/Button';
import { InputFecha } from '../ui/InputFecha';
import { Monto, Num } from './Monto';

export interface DepositosViewProps {
  depositos: DepositoAPlazo[];
  totalVigente: number;
  /** Último saldo de la cuenta corriente, para el "Total barrio". */
  saldoCorriente: number | null;
  loading: boolean;
  error: string | null;
  onCrear: (data: CrearDepositoIn) => Promise<string | null>;
  onLiquidar: (id: string, data: LiquidarDepositoIn) => Promise<string | null>;
}

function tasaPct(bps: number | null): string {
  if (bps === null) return '—';
  return `${(bps / 100).toFixed(2).replace('.', ',')}%`;
}

export function DepositosView({
  depositos,
  totalVigente,
  saldoCorriente,
  loading,
  error,
  onCrear,
  onLiquidar,
}: DepositosViewProps) {
  const [crearOpen, setCrearOpen] = useState(false);
  const [liquidando, setLiquidando] = useState<DepositoAPlazo | null>(null);

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <p className="text-sm text-text-muted max-w-2xl">
          Depósitos a plazo del barrio. El capital sale de la cuenta corriente
          (no es gasto: es un traspaso) y vuelve con el interés al liquidar.
        </p>
        <Button variant="primary" type="button" onClick={() => setCrearOpen(true)}>
          Registrar depósito
        </Button>
      </div>

      {/* Totales */}
      <div className="flex gap-3 flex-wrap">
        <div className="bg-bg-card border-[0.5px] border-white/[0.08] rounded-md p-4">
          <div className="text-[10px] uppercase tracking-wide text-text-muted">
            En depósitos (vigentes)
          </div>
          <div className="text-xl font-medium text-text-primary mt-1">
            <Monto value={totalVigente} />
          </div>
        </div>
        {saldoCorriente !== null && (
          <div className="bg-bg-card border-[0.5px] border-white/[0.08] rounded-md p-4">
            <div className="text-[10px] uppercase tracking-wide text-text-muted">
              Total barrio (corriente + depósitos)
            </div>
            <div className="text-xl font-medium text-emerald-400 mt-1">
              <Monto value={saldoCorriente + totalVigente} />
            </div>
          </div>
        )}
      </div>

      {loading && <p className="text-sm text-text-muted">Cargando…</p>}
      {error && !loading && (
        <div
          className="bg-state-danger/15 border-l-4 border-state-danger p-3 rounded-sm text-sm text-text-primary"
          role="alert"
        >
          {error}
        </div>
      )}

      {!loading && !error && depositos.length === 0 && (
        <p className="text-sm text-text-muted">
          No hay depósitos a plazo registrados.
        </p>
      )}

      {!loading && !error && depositos.length > 0 && (
        <div className="overflow-x-auto bg-bg-card border-[0.5px] border-white/[0.08] rounded-md">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wide text-text-secondary border-b border-white/[0.08]">
              <tr>
                <th className="text-left px-3 py-2 font-medium">Banco</th>
                <th className="text-right px-3 py-2 font-medium">Capital</th>
                <th className="text-left px-3 py-2 font-medium">Apertura</th>
                <th className="text-left px-3 py-2 font-medium">Vence</th>
                <th className="text-right px-3 py-2 font-medium">Tasa</th>
                <th className="text-left px-3 py-2 font-medium">Estado</th>
                <th className="text-right px-3 py-2 font-medium">Interés</th>
                <th className="text-right px-3 py-2 font-medium">Acción</th>
              </tr>
            </thead>
            <tbody>
              {depositos.map((d) => (
                <tr
                  key={d.id}
                  className="border-b border-white/[0.04] hover:bg-bg-card/40"
                >
                  <td className="px-3 py-2 text-text-primary">
                    {d.banco}
                    {d.folio && (
                      <span className="text-text-muted text-xs">
                        {' '}· <Num>{d.folio}</Num>
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right text-text-primary">
                    <Monto value={d.capital} />
                  </td>
                  <td className="px-3 py-2 text-text-muted">
                    {formatFecha(d.fecha_apertura)}
                  </td>
                  <td className="px-3 py-2 text-text-muted">
                    {formatFecha(d.fecha_vencimiento)}
                    {d.renovable && (
                      <span className="text-[10px] text-text-muted/70"> (renov.)</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right text-text-secondary">
                    {tasaPct(d.tasa_bps)}
                  </td>
                  <td
                    className={`px-3 py-2 font-medium ${
                      d.estado === 'vigente'
                        ? 'text-state-success'
                        : 'text-text-muted'
                    }`}
                  >
                    {d.estado === 'vigente' ? 'Vigente' : 'Liquidado'}
                  </td>
                  <td className="px-3 py-2 text-right text-text-secondary">
                    {d.interes_ganado !== null ? (
                      <Monto value={d.interes_ganado} />
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {d.estado === 'vigente' && (
                      <button
                        type="button"
                        onClick={() => setLiquidando(d)}
                        className="text-xs text-state-success hover:underline"
                      >
                        Liquidar
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {crearOpen && (
        <ModalCrearDeposito
          onConfirmar={onCrear}
          onCerrar={() => setCrearOpen(false)}
        />
      )}
      {liquidando && (
        <ModalLiquidar
          deposito={liquidando}
          onConfirmar={(data) => onLiquidar(liquidando.id, data)}
          onCerrar={() => setLiquidando(null)}
        />
      )}
    </div>
  );
}

function ModalCrearDeposito({
  onConfirmar,
  onCerrar,
}: {
  onConfirmar: (data: CrearDepositoIn) => Promise<string | null>;
  onCerrar: () => void;
}) {
  const [banco, setBanco] = useState('Banco de Chile');
  const [capital, setCapital] = useState('');
  const [fechaApertura, setFechaApertura] = useState(hoy());
  const [plazoDias, setPlazoDias] = useState('30');
  const [tasaPctStr, setTasaPctStr] = useState('');
  const [renovable, setRenovable] = useState(true);
  const [folio, setFolio] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const capitalNum = Number.parseInt(capital, 10);
  const plazoNum = Number.parseInt(plazoDias, 10);
  const valido =
    banco.trim().length > 0 &&
    Number.isFinite(capitalNum) &&
    capitalNum > 0 &&
    Number.isFinite(plazoNum) &&
    plazoNum > 0 &&
    !!fechaApertura;

  async function handleConfirmar() {
    if (!valido || submitting) return;
    setSubmitting(true);
    setError(null);
    const tasaPctNum = Number.parseFloat(tasaPctStr.replace(',', '.'));
    const tasa_bps =
      tasaPctStr.trim() && Number.isFinite(tasaPctNum)
        ? Math.round(tasaPctNum * 100)
        : null;
    const err = await onConfirmar({
      banco: banco.trim(),
      capital: capitalNum,
      fecha_apertura: fechaApertura,
      plazo_dias: plazoNum,
      tasa_bps,
      renovable,
      folio: folio.trim() || null,
    });
    setSubmitting(false);
    if (err) setError(err);
    else onCerrar();
  }

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50"
      role="dialog"
      aria-modal="true"
    >
      <div className="bg-bg-card border-[0.5px] border-white/15 rounded-md p-6 max-w-md w-full space-y-4">
        <div className="flex items-start justify-between">
          <h2 className="text-lg font-medium text-text-primary">
            Registrar depósito a plazo
          </h2>
          <button
            type="button"
            onClick={onCerrar}
            disabled={submitting}
            className="text-text-muted hover:text-text-primary text-xl leading-none"
            aria-label="Cerrar"
          >
            ×
          </button>
        </div>
        <p className="text-xs text-text-muted">
          El capital se traspasa de la cuenta corriente. El vencimiento se
          calcula con apertura + plazo.
        </p>

        <label className="block text-sm space-y-1">
          <span className="text-text-secondary">Banco</span>
          <input
            value={banco}
            onChange={(e) => setBanco(e.target.value)}
            disabled={submitting}
            className="w-full rounded-sm bg-bg-sunken border-[0.5px] border-white/15 text-text-primary px-3 py-2"
          />
        </label>

        <label className="block text-sm space-y-1">
          <span className="text-text-secondary">
            Capital (CLP) <span className="text-state-danger">*</span>
          </span>
          <input
            type="number"
            inputMode="numeric"
            value={capital}
            onChange={(e) => setCapital(e.target.value)}
            disabled={submitting}
            placeholder="Ej: 3000000"
            className="num w-full rounded-sm bg-bg-sunken border-[0.5px] border-white/15 text-text-primary px-3 py-2"
          />
        </label>

        <div className="flex gap-3">
          <label className="block text-sm space-y-1 flex-1">
            <span className="text-text-secondary">Fecha apertura</span>
            <InputFecha value={fechaApertura} onChange={setFechaApertura} />
          </label>
          <label className="block text-sm space-y-1 w-28">
            <span className="text-text-secondary">Plazo (días)</span>
            <input
              type="number"
              inputMode="numeric"
              value={plazoDias}
              onChange={(e) => setPlazoDias(e.target.value)}
              disabled={submitting}
              className="num w-full rounded-sm bg-bg-sunken border-[0.5px] border-white/15 text-text-primary px-3 py-2"
            />
          </label>
        </div>

        <div className="flex gap-3 items-end">
          <label className="block text-sm space-y-1 w-32">
            <span className="text-text-secondary">Tasa (% periodo)</span>
            <input
              type="text"
              inputMode="decimal"
              value={tasaPctStr}
              onChange={(e) => setTasaPctStr(e.target.value)}
              disabled={submitting}
              placeholder="0,45"
              className="num w-full rounded-sm bg-bg-sunken border-[0.5px] border-white/15 text-text-primary px-3 py-2"
            />
          </label>
          <label className="block text-sm space-y-1 flex-1">
            <span className="text-text-secondary">Folio / N° operación</span>
            <input
              value={folio}
              onChange={(e) => setFolio(e.target.value)}
              disabled={submitting}
              className="w-full rounded-sm bg-bg-sunken border-[0.5px] border-white/15 text-text-primary px-3 py-2"
            />
          </label>
        </div>

        <label className="flex items-center gap-2 text-sm text-text-secondary">
          <input
            type="checkbox"
            checked={renovable}
            onChange={(e) => setRenovable(e.target.checked)}
            disabled={submitting}
          />
          Renovable automáticamente
        </label>

        {error && (
          <div
            className="bg-state-danger/15 border-l-4 border-state-danger p-3 rounded-sm text-sm text-text-primary"
            role="alert"
          >
            {error}
          </div>
        )}
        <div className="flex justify-end gap-2 pt-1">
          <Button variant="ghost" type="button" onClick={onCerrar} disabled={submitting}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            type="button"
            onClick={handleConfirmar}
            disabled={!valido || submitting}
          >
            {submitting ? 'Registrando…' : 'Registrar'}
          </Button>
        </div>
      </div>
    </div>
  );
}

function ModalLiquidar({
  deposito,
  onConfirmar,
  onCerrar,
}: {
  deposito: DepositoAPlazo;
  onConfirmar: (data: LiquidarDepositoIn) => Promise<string | null>;
  onCerrar: () => void;
}) {
  const [fechaLiq, setFechaLiq] = useState(hoy());
  const [interes, setInteres] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const interesNum = interes.trim() ? Number.parseInt(interes, 10) : 0;
  const valido = !!fechaLiq && Number.isFinite(interesNum) && interesNum >= 0;

  async function handleConfirmar() {
    if (!valido || submitting) return;
    setSubmitting(true);
    setError(null);
    const err = await onConfirmar({
      fecha_liquidacion: fechaLiq,
      interes_ganado: interesNum,
    });
    setSubmitting(false);
    if (err) setError(err);
    else onCerrar();
  }

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50"
      role="dialog"
      aria-modal="true"
    >
      <div className="bg-bg-card border-[0.5px] border-white/15 rounded-md p-6 max-w-md w-full space-y-4">
        <div className="flex items-start justify-between">
          <h2 className="text-lg font-medium text-text-primary">
            Liquidar depósito
          </h2>
          <button
            type="button"
            onClick={onCerrar}
            disabled={submitting}
            className="text-text-muted hover:text-text-primary text-xl leading-none"
            aria-label="Cerrar"
          >
            ×
          </button>
        </div>
        <p className="text-sm text-text-secondary">
          {deposito.banco} · capital <Monto value={deposito.capital} />. El
          capital + interés vuelven a la cuenta corriente. El interés (si lo
          indicas) se registra como ingreso del barrio.
        </p>

        <div className="flex gap-3">
          <label className="block text-sm space-y-1 flex-1">
            <span className="text-text-secondary">Fecha liquidación</span>
            <InputFecha value={fechaLiq} onChange={setFechaLiq} />
          </label>
          <label className="block text-sm space-y-1 flex-1">
            <span className="text-text-secondary">Interés ganado (CLP)</span>
            <input
              type="number"
              inputMode="numeric"
              value={interes}
              onChange={(e) => setInteres(e.target.value)}
              disabled={submitting}
              placeholder="0"
              className="num w-full rounded-sm bg-bg-sunken border-[0.5px] border-white/15 text-text-primary px-3 py-2"
            />
          </label>
        </div>

        {error && (
          <div
            className="bg-state-danger/15 border-l-4 border-state-danger p-3 rounded-sm text-sm text-text-primary"
            role="alert"
          >
            {error}
          </div>
        )}
        <div className="flex justify-end gap-2 pt-1">
          <Button variant="ghost" type="button" onClick={onCerrar} disabled={submitting}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            type="button"
            onClick={handleConfirmar}
            disabled={!valido || submitting}
          >
            {submitting ? 'Liquidando…' : 'Liquidar'}
          </Button>
        </div>
      </div>
    </div>
  );
}
